import { type CruiseListItem, type CruiseListQuery } from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  UzConfirmDialog,
  UzDropdownMenu,
  UzInput,
  UzSelect,
  UzStatusBadge,
} from '@utazzo/ui';
import { motion } from 'framer-motion';
import {
  Copy,
  FileSpreadsheet,
  Image as ImageIcon,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CruiseCard } from '../components/CruiseCard';
import { listCruises } from '../cruises.api';
import {
  useBulkDeleteCruises,
  useCruisesList,
  useDeleteCruise,
  useDuplicateCruise,
} from '../use-cruises';

import {
  DataGrid,
  DataGridPagination,
  type DataGridColumn,
  type DataGridFilter,
} from '@/components/data-grid';
import { useListScreen } from '@/components/list-screen';
import { downloadExcel, fetchAllPages } from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { stagger } from '@/lib/motion';

/**
 * Cruise master list (Cruise-as-stay phase 1).
 *
 * Same two-view identity as Hotels / Activities / Transfers:
 *   - Grid (default) - CruiseCard grid, optimised for browsing/picking.
 *   - List - DataGrid with the full column set + sort + density, optimised
 *     for ops + bulk actions.
 *
 * Both modes share the same `useListScreen` state so search, filters, sort,
 * page stay in sync across the toggle. Not routed yet (Task 9 wires nav).
 */

// ---------------------------------------------------------------------------
// Filter dropdown options
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const STAR_OPTIONS = [
  { value: '', label: 'All ratings' },
  { value: '5', label: '5 star' },
  { value: '4', label: '4 star' },
  { value: '3', label: '3 star' },
  { value: '2', label: '2 star' },
  { value: '1', label: '1 star' },
] as const;

const APPROVAL_OPTIONS = [
  { value: '', label: 'All approvals' },
  { value: 'approved', label: 'Approved only' },
  { value: 'pending', label: 'Not approved' },
] as const;

const ARCHIVED_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'archived', label: 'Include archived' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'stars',
    label: 'Stars',
    options: STAR_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-36',
  },
  {
    id: 'approval',
    label: 'Approval',
    options: APPROVAL_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'archived',
    label: 'Archived',
    options: ARCHIVED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
];

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const renderStarsAscii = (rating: number): string =>
  rating <= 0 ? '-' : '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating));

const mapStatus = (s: CruiseListItem['status']): 'active' | 'inactive' | 'draft' | 'pending' => {
  switch (s) {
    case 'ACTIVE':
      return 'active';
    case 'DRAFT':
      return 'draft';
    case 'PENDING_APPROVAL':
      return 'pending';
    default:
      return 'inactive';
  }
};

/** "From" price in its own currency (SGD/AED/EUR/INR...), whole units. */
const formatFromPrice = (price: number, currency: string | null): string => {
  const code = currency ?? 'INR';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(price);
  } catch {
    return `${code} ${Math.round(price).toLocaleString('en-IN')}`;
  }
};

// ---------------------------------------------------------------------------
// View toggle
// ---------------------------------------------------------------------------

type View = 'grid' | 'list';

interface ViewToggleProps {
  view: View;
  onChange: (next: View) => void;
}

const ViewToggle = ({ view, onChange }: ViewToggleProps): React.JSX.Element => {
  const btn = (target: View, Icon: typeof LayoutGrid, label: string): React.JSX.Element => (
    <button
      type="button"
      role="tab"
      aria-selected={view === target}
      onClick={() => onChange(target)}
      className={
        'relative inline-flex h-7 items-center gap-1.5 rounded-[5px] px-2.5 text-xs font-medium transition-colors duration-150 ' +
        (view === target
          ? 'bg-white text-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-zinc-200'
          : 'text-zinc-500 hover:text-zinc-800')
      }
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );

  return (
    <div role="tablist" aria-label="View mode" className="inline-flex rounded-md bg-zinc-100 p-0.5">
      {btn('grid', LayoutGrid, 'Grid')}
      {btn('list', ListIcon, 'List')}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Grid-mode toolbar (search + filter dropdowns + clear). List mode uses
// DataGrid's built-in toolbar so we don't render this above it.
// ---------------------------------------------------------------------------

interface GridToolbarProps {
  search: string;
  onSearchChange: (q: string) => void;
  filterState: Record<string, string>;
  onFilterChange: (id: string, value: string) => void;
  resultCount: number;
}

const GridToolbar = ({
  search,
  onSearchChange,
  filterState,
  onFilterChange,
  resultCount,
}: GridToolbarProps): React.JSX.Element => {
  const activeFilters = FILTERS.filter((f) => Boolean(filterState[f.id]));
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <UzInput
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search cruises..."
        leftAdornment={<Search className="h-3.5 w-3.5" aria-hidden />}
        containerClassName="w-64"
      />
      {FILTERS.map((f) => (
        <UzSelect
          key={f.id}
          value={filterState[f.id] ?? ''}
          onValueChange={(v) => onFilterChange(f.id, v)}
          options={f.options as ReadonlyArray<{ value: string; label: string }>}
          placeholder={f.label}
          triggerClassName="h-8 min-w-[140px] text-xs"
        />
      ))}
      {activeFilters.length > 0 && (
        <UzButton
          variant="ghost"
          size="sm"
          onClick={() => FILTERS.forEach((f) => onFilterChange(f.id, ''))}
          className="text-xs text-zinc-500 hover:text-zinc-900"
        >
          Clear · {activeFilters.length}
        </UzButton>
      )}
      <span className="flex-1" />
      <span className="text-xs text-zinc-500 tabular-nums">
        <span className="font-medium text-zinc-700">{resultCount}</span>
        {' results'}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const CruisesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCruise = useDeleteCruise();
  const bulkDeleteMut = useBulkDeleteCruises();
  const duplicateCruise = useDuplicateCruise();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');

  const screen = useListScreen<CruiseListItem, CruiseListQuery>({
    title: 'Cruises',
    useList: useCruisesList,
    defaultSort: { by: 'createdAt', dir: 'desc' },
    minSearchLength: MIN_SEARCH_LENGTH,
    filters: FILTERS,
    getRowId: (row) => row.id,
    columns: [], // unused; DataGrid columns are passed inline below
    toQuery: (state, base) => {
      const status = state.filters.status;
      const stars = state.filters.stars;
      const approval = state.filters.approval;
      const archived = state.filters.archived;
      return {
        ...base,
        ...(status ? { status: status as CruiseListQuery['status'] } : {}),
        ...(stars ? { starRating: Number(stars) } : {}),
        ...(approval === 'approved' ? { isApprovedByAdmin: true } : {}),
        ...(approval === 'pending' ? { isApprovedByAdmin: false } : {}),
        ...(archived === 'archived' ? { includeDeleted: true } : {}),
      };
    },
  });

  const { state, gridState, query, list, data, pagination, deletion } = screen;

  // Inline columns for list mode: cover, name, cruise line, homeport city,
  // cabins count, from-price (lowest active adult fare), status.
  const listColumns: ReadonlyArray<DataGridColumn<CruiseListItem>> = [
    {
      id: 'serial',
      header: 'S.No',
      width: '64px',
      cell: (_row, index) => (
        <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
      ),
    },
    {
      id: 'cover',
      header: '',
      width: '48px',
      resizable: false,
      cell: () => (
        <span className="flex h-8 w-8 items-center justify-center rounded bg-zinc-50 ring-1 ring-zinc-200/80">
          <ImageIcon className="h-4 w-4 text-zinc-300" strokeWidth={1.5} aria-hidden />
        </span>
      ),
    },
    {
      id: 'name',
      header: 'Cruise',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-zinc-900">{row.name}</span>
          <span className="font-mono text-xs text-amber-500">
            {renderStarsAscii(row.starRating)}
          </span>
        </div>
      ),
    },
    {
      id: 'cruiseLine',
      header: 'Cruise line',
      cell: (row) => (
        <span className="text-zinc-600">
          {row.cruiseLine ?? <span className="text-zinc-400">-</span>}
        </span>
      ),
    },
    {
      id: 'homeport',
      header: 'Homeport',
      cell: (row) => (
        <div className="flex flex-col text-sm">
          <span className="text-zinc-900">
            {row.cityName ?? <span className="text-zinc-400">-</span>}
          </span>
          <span className="text-xs text-zinc-500">
            {row.countryName ?? <span className="text-zinc-400">-</span>}
          </span>
        </div>
      ),
    },
    {
      id: 'cabins',
      header: 'Cabins',
      width: '90px',
      cell: (row) => (
        <UzBadge tone={row.cabinCount > 0 ? 'secondary' : 'default'}>
          {row.cabinCount} cabin{row.cabinCount === 1 ? '' : 's'}
        </UzBadge>
      ),
    },
    {
      id: 'fromPrice',
      header: 'From',
      width: '120px',
      cell: (row) =>
        row.fromPrice !== null ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] tracking-wide text-zinc-400 uppercase">From</span>
            <span className="font-semibold text-zinc-900 tabular-nums">
              {formatFromPrice(row.fromPrice, row.fromPriceCurrency)}
            </span>
          </div>
        ) : (
          <span className="text-zinc-400">-</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '140px',
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <UzStatusBadge status={mapStatus(row.status)} />
          {row.isApprovedByAdmin && (
            <span className="text-[10px] tracking-wide text-emerald-600 uppercase">Approved</span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '52px',
      align: 'center',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => (
        <div
          className="flex justify-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <UzDropdownMenu
            align="end"
            trigger={
              <UzButton variant="ghost" size="icon" aria-label="Row actions">
                <MoreHorizontal className="h-4 w-4" />
              </UzButton>
            }
            items={[
              {
                label: 'Duplicate to draft',
                icon: <Copy className="h-4 w-4" />,
                disabled: duplicateCruise.isPending,
                onSelect: () => void duplicateCruise.mutateAsync(row.id),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const onOpenCruise = (row: CruiseListItem): void => {
    navigate(`/cruises/${row.id}/edit`);
  };

  return (
    <PageShell
      title="Cruises"
      description="Cruise-as-stay catalog."
      actions={
        // `contents` keeps the buttons laid out by the header exactly as before;
        // on phones this becomes a real row pinned to the <main> content width
        // (viewport minus its px-4) so it wraps instead of running off-screen.
        <div className="contents max-sm:flex max-sm:w-[calc(100vw-2rem)] max-sm:flex-wrap max-sm:items-center max-sm:gap-2">
          <ViewToggle view={view} onChange={setView} />
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => void list.refetch()}
            loading={list.isFetching}
            aria-label="Refresh"
          >
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Refresh
          </UzButton>
          <UzButton variant="outline" size="sm" onClick={() => navigate('/cruises/imports')}>
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />
            Bulk import
          </UzButton>
          <UzButton onClick={() => navigate('/cruises/new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add cruise
          </UzButton>
        </div>
      }
    >
      {view === 'list' ? (
        <DataGrid<CruiseListItem>
          columns={listColumns}
          data={data}
          pagination={pagination}
          isLoading={list.isLoading}
          isError={list.isError}
          error={{
            title: 'Failed to load cruises',
            description: 'The server returned an error. Try refreshing or check your network.',
          }}
          onRetry={() => void list.refetch()}
          state={state}
          onStateChange={{
            setSearch: gridState.setSearch,
            setFilter: gridState.setFilter,
            setSort: gridState.setSort,
            setPage: gridState.setPage,
            setPageSize: gridState.setPageSize,
            setDensity: gridState.setDensity,
            setHidden: gridState.setHidden,
          }}
          filters={FILTERS}
          getRowId={(row) => row.id}
          onRowClick={onOpenCruise}
          searchPlaceholder="Search by name, cruise line, or address (min 3 characters)"
          emptyTitle="No cruises match these filters"
          emptyDescription="Adjust filters or clear the search box."
          selectable
          bulkActions={[
            {
              id: 'bulk-delete',
              label: 'Delete',
              danger: true,
              onAction: (_ids, rows) => {
                void bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
              },
            },
          ]}
          onExport={async () => {
            const rows = await fetchAllPages(listCruises, query);
            downloadExcel(
              rows,
              [
                { header: 'Name', value: (r) => r.name },
                { header: 'Cruise line', value: (r) => r.cruiseLine ?? '' },
                { header: 'Homeport city', value: (r) => r.cityName ?? '' },
                { header: 'Homeport country', value: (r) => r.countryName ?? '' },
                { header: 'Stars', value: (r) => r.starRating },
                { header: 'Cabins', value: (r) => r.cabinCount },
                { header: 'Vendor', value: (r) => r.vendorSupplierName ?? '' },
                { header: 'Approved', value: (r) => (r.isApprovedByAdmin ? 'Yes' : 'No') },
                { header: 'Status', value: (r) => r.status },
              ],
              'cruises',
            );
          }}
          tableLayout="auto"
        />
      ) : (
        <>
          <GridToolbar
            search={state.q}
            onSearchChange={gridState.setSearch}
            filterState={state.filters}
            onFilterChange={gridState.setFilter}
            resultCount={pagination?.totalItems ?? 0}
          />

          {list.isLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[228px] animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60"
                />
              ))}
            </div>
          ) : list.isError ? (
            <div className="rounded-lg border border-zinc-200 bg-white p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900">Failed to load cruises</h3>
              <p className="mx-auto mt-1 max-w-sm text-xs text-zinc-500">
                The server returned an error. Check your connection and try again.
              </p>
              <UzButton
                size="sm"
                variant="outline"
                onClick={() => void list.refetch()}
                className="mt-4"
              >
                Retry
              </UzButton>
            </div>
          ) : data.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900">No cruises match</h3>
              <p className="mx-auto mt-1 max-w-sm text-xs text-zinc-500">
                Adjust filters or clear the search box.
              </p>
            </div>
          ) : (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {data.map((cruise) => (
                <CruiseCard key={cruise.id} cruise={cruise} onClick={onOpenCruise} />
              ))}
            </motion.div>
          )}

          {pagination && data.length > 0 && (
            <div className="mt-4 rounded-md border border-zinc-200 bg-white">
              <DataGridPagination
                page={pagination.page}
                pageSize={pagination.pageSize}
                totalItems={pagination.totalItems}
                onPageChange={gridState.setPage}
                onPageSizeChange={gridState.setPageSize}
              />
            </div>
          )}
        </>
      )}

      {canDelete && (
        <UzConfirmDialog
          open={deletion.target !== null}
          onOpenChange={(open) => {
            if (!open) deletion.clear();
          }}
          title="Archive this cruise?"
          description={
            deletion.target
              ? `${deletion.target.name} will be soft-deleted, status set to ARCHIVED, and hidden from lists.`
              : ''
          }
          confirmLabel="Archive"
          tone="destructive"
          onConfirm={async () => {
            if (deletion.target) {
              await deleteCruise.mutateAsync(deletion.target.id);
              deletion.clear();
            }
          }}
        />
      )}
    </PageShell>
  );
};
